import React, {useState, useContext} from 'react';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';

import {addToast, Text, Icon, Button, IconButton, WithTooltip, Anchor} from '@albert-io/atomic';

import {GoogleContext} from 'client/components/Google';
import {withStopPropagation} from './utils';

import './google-cell.scss';

type GoogleContextType = {
  isRequestPending: boolean;
  isSignedIn: boolean;
  handleExportAssignment: (...args: any[]) => void;
};

interface GoogleCellProps {
  assignment: AssignmentModelV3;
  googleCourseId: string;
  url: string;
}

export const GoogleCell = ({assignment, googleCourseId, url}: GoogleCellProps) => {
  const {isSignedIn, handleExportAssignment} = useContext<GoogleContextType>(GoogleContext);

  const [hasPostedToGoogleClassroom, setHasPostedToGoogleClassroom] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);

  const linkHref =
    'https://help.albert.io/en/articles/1885029-how-do-i-sync-my-albert-classroom-with-a-google-classroom';

  const exportAssignment = () => {
    setIsRequestPending(true);
    handleExportAssignment({
      assignment,
      assignmentLink: url,
      googleCourseId,
      successCallback: () => {
        addToast({
          title: 'Success!',
          type: 'success',
          message: 'Your assignment has been posted to Google Classroom'
        });
        setHasPostedToGoogleClassroom(true);
        setIsRequestPending(false);
      },
      errorCallback: () => {
        addToast({
          type: 'danger',
          message: 'There was an error posting to Google Classroom.'
        });
        setIsRequestPending(false);
      }
    });
  };

  if (!isSignedIn) {
    return (
      <WithTooltip
        theme='light'
        placement='left'
        content={
          <div>
            {'Cannot post. Sign in to Google first '}
            <Anchor target='_blank' to={linkHref}>
              Learn More
            </Anchor>
          </div>
        }
        wrapperProps={{className: 'u-justify-self_center'}}
      >
        <IconButton
          onClick={withStopPropagation(() => {})}
          icon='info-circle'
          iconStyle='regular'
          aria-hidden={false}
        />
      </WithTooltip>
    );
  }

  if (!googleCourseId) {
    return (
      <WithTooltip
        theme='light'
        placement='left'
        content={
          <div>
            {'Cannot post. Connect this classroom to a Google classroom first. '}
            <Anchor target='_blank' to={linkHref}>
              Learn More
            </Anchor>
          </div>
        }
        wrapperProps={{className: 'u-justify-self_center'}}
      >
        <IconButton
          onClick={withStopPropagation(() => {})}
          icon='info-circle'
          iconStyle='regular'
          aria-hidden={false}
        />
      </WithTooltip>
    );
  }

  if (hasPostedToGoogleClassroom) {
    return (
      <Text color='green' bold className='u-justify-self_center u-color_green-600'>
        Posted <Icon className='u-mar-l_1' icon='check' color='inherit' />
      </Text>
    );
  }

  return (
    <Button
      disabled={isRequestPending}
      size='s'
      variant='outlined'
      color='primary'
      className='google-cell__button'
      onClick={exportAssignment}
    >
      Post
    </Button>
  );
};
